import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import logo from "../assets/logo.png";
import { FaBars, FaBolt, FaPhone, FaXmark } from "react-icons/fa6";

export default function TopBar() {
  const appContext = useContext(AppContext);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);


  useEffect(() => {
    setMobileMenuOpen(false);
  }, [appContext.navItem]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const navItems = [
    { id: "home", label: "Home" },
    { id: "about_us", label: "About" },
    { id: "services", label: "Services" },
    { id: "contact_us", label: "Contact" },
  ];

  const handleNav = (item) => {
    appContext.navTo({ item });
    setMobileMenuOpen(false);
  };

  // Color constants - NO WHITE for nav items
  const COLORS = {
    navDefault: "#334155",      // slate-700 - default nav text
    navHover: "#FF9700",        // primary blue - hover/active
    navActive: "#FF9700",       // primary blue - active state
    logoText: "#0f172a",        // slate-900 - logo text
    toggleIcon: "#334155",      // slate-700 - mobile toggle
  };

  return (
    <>
      {/* Top Bar */}
      <header
        className="TopBar position-fixed w-100 top-0 start-0 z-3"
        style={{
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
          transition: "all 0.25s ease",
        }}
      >
        <div className="container px-3 px-md-4">
          <div className="d-flex align-items-center justify-content-between" style={{ minHeight: "70px" }}>
            
            {/* Logo */}
            <div
              className="d-flex align-items-center gap-3"
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={() => handleNav("home")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleNav("home")}
            >
              <img
                src={logo}
                alt="Medal Global Engineering"
                style={{ height: "42px", width: "auto", objectFit: "contain" }}
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="d-none d-lg-flex align-items-center" role="navigation">
              {navItems.map((item) => {
                const isActive =
                  appContext.navItem === item.id ||
                  (!appContext.navItem && item.id === "home");
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNav(item.id)}
                    className="position-relative px-4 py-2 bg-transparent border-0"
                    style={{
                      color: isActive ? COLORS.navActive : COLORS.navDefault,
                      fontSize: "0.95rem",
                      fontWeight: isActive ? "600" : "500",
                      cursor: "pointer",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = COLORS.navHover;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = COLORS.navDefault;
                      }
                    }}
                  >
                    {item.label}
                    {/* Active/Hover indicator - subtle bottom bar */}
                    <span
                      className="position-absolute bottom-0 start-50 translate-middle-x"
                      style={{
                        width: isActive ? "24px" : "0",
                        height: "2px",
                        background: COLORS.navActive,
                        borderRadius: "2px",
                        transition: "width 0.2s ease",
                        opacity: isActive ? 1 : 0,
                      }}
                    />
                  </button>
                );
              })}
            </nav>

            {/* Desktop CTA + Mobile Toggle */}
            <div className="d-flex align-items-center gap-3">
              {/* Desktop CTA */}
              <a
                href="tel:+255782665569"
                className="d-none d-lg-flex align-items-center gap-2 btn rounded-pill px-4 py-2 fw-medium"
                style={{
                  backgroundColor: "#FF9700",
                  color: "#fff",
                  border: "none",
                  boxShadow: "0 2px 8px rgba(59, 130, 246, 0.2)",
                  transition: "all 0.2s ease",
                  fontSize: "0.9rem",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#2563EB";
                  e.currentTarget.style.boxShadow = "0 4px 14px rgba(59, 130, 246, 0.3)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#FF9700";
                  e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.2)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <FaPhone size={14} />
                <span className="d-none d-xl-inline">+255 782 665 569</span>
                <span className="d-xl-none">Call</span>
              </a>

              {/* Mobile Menu Toggle */}
              <button
                className="d-lg-none btn btn-link text-decoration-none p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                style={{
                  color: COLORS.toggleIcon,
                  fontSize: "1.3rem",
                  transition: "color 0.25s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {mobileMenuOpen ? <FaXmark /> : <FaBars className="d-none" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100 z-2"
            onClick={() => setMobileMenuOpen(false)}
            style={{ 
              animation: "fadeIn 0.2s ease",
              background: "rgba(15, 23, 42, 0.5)",
              backdropFilter: "blur(4px)",
            }}
          />
          
          {/* Menu Panel */}
          <div
            className="position-fixed top-0 end-0 h-100 bg-white z-3"
            style={{
              width: "85%",
              maxWidth: "340px",
              animation: "slideInRight 0.25s ease",
              overflowY: "auto",
              boxShadow: "-4px 0 24px rgba(0,0,0,0.08)",
            }}
          >
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between p-4" style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
              <div className="d-flex align-items-center gap-2">
                <img src={logo} alt="MGE" style={{ height: "34px" }} />
                <span className="fw-bold" style={{ color: COLORS.logoText, fontSize: "0.95rem" }}>Medal Global Engineering</span>
              </div>
              <button
                className="btn btn-link text-decoration-none p-1"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                style={{ fontSize: "1.2rem", color: COLORS.navDefault }}
              >
                <FaXmark />
              </button>
            </div>

            {/* Navigation */}
            <nav className="p-4" role="navigation">
              <div className="d-flex flex-column gap-1">
                {navItems.map((item) => {
                  const isActive =
                    appContext.navItem === item.id ||
                    (!appContext.navItem && item.id === "home");
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNav(item.id)}
                      className="text-start px-3 py-3 rounded-3 border-0 bg-transparent fw-medium d-flex align-items-center justify-content-between"
                      style={{
                        color: isActive ? COLORS.navActive : "COLORS.navDefault",
                        fontSize: "1rem",
                        background: isActive ? "rgba(59, 130, 246, 0.06)" : "transparent",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = "rgba(59, 130, 246, 0.04)";
                          e.currentTarget.style.color = COLORS.navHover;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = COLORS.navDefault;
                        }
                      }}
                    >
                      <span>{item.label}</span>
                      {isActive && (
                        <span style={{ color: COLORS.navActive, fontSize: "1.1rem" }}>•</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Mobile CTA */}
              <div className="mt-6 pt-4" style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                <a
                  href="tel:+255782665569"
                  className="btn btn-primary w-100 rounded-pill py-3 fw-medium d-flex align-items-center justify-content-center gap-2"
                  style={{ 
                    backgroundColor: "#FF9700", 
                    border: "none", 
                    fontSize: "0.95rem",
                    boxShadow: "0 2px 8px rgba(59, 130, 246, 0.2)",
                  }}
                >
                  <FaPhone size={15} />
                  Call: +255 782 665 569
                </a>
                <a
                  href="mailto:info@mge.com"
                  className="btn btn-outline-secondary w-100 rounded-pill py-3 fw-medium mt-3"
                  style={{ 
                    fontSize: "0.95rem",
                    borderColor: "rgba(0,0,0,0.15)",
                    color: COLORS.navDefault,
                  }}
                >
                  Email: info@mge.com
                </a>
              </div>

              {/* Footer Info */}
              <div className="mt-auto p-4 text-center" style={{ color: "#94a3b8", fontSize: "0.8rem" }}>
                <p className="mb-1">© Medal Global Engineering {new Date().getFullYear()}</p>
                <p className="mb-0">Dar es Salaam, Tanzania</p>
              </div>
            </nav>
          </div>
        </>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .btn-link:hover {
          color: #FF9700 !important;
        }
        .btn-outline-secondary:hover {
          background: rgba(59, 130, 246, 0.04) !important;
          border-color: #FF9700 !important;
          color: #FF9700 !important;
        }
      `}</style>
    </>
  );
}